import { Locale } from '@frontend/common';
import { CommonMessage, getCorrectTranslation } from '@frontend/lang';
import { IProduct } from '@frontend/product/types';
import React from 'react';
import { LuChevronDown, LuChevronRight, LuFolder } from 'react-icons/lu';

import { CategoryNode } from './directory-view.controller';
import { ProductRow } from './product-row.component';

export interface CategoryRowProps {
    node: CategoryNode;
    depth: number;
    path: string[];
    expandedCategories: Set<string>;
    onToggle: (path: string[]) => void;
    onCategoryClick: (category: CategoryNode['category']) => void;
    onProductClick: (product: IProduct) => void;
    locale: Locale | undefined;
    isLast?: boolean;
}

export const CategoryRow: React.FC<CategoryRowProps> = ({
    node,
    depth,
    path,
    expandedCategories,
    onToggle,
    onCategoryClick,
    onProductClick,
    locale,
    isLast = false
}) => {
    const hasChildren = node.subcategoriesCount > 0 || node.productsCount > 0;
    const pathKey = path.join('/');
    const isExpanded = expandedCategories.has(pathKey);
    const indentLevel = depth * 50;

    const hasVisibleChildren = isExpanded && (node.subcategories.length > 0 || node.products.length > 0 || node.isLoading);
    const shouldShowBorder = !(isLast && !hasVisibleChildren);

    const handleToggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (hasChildren) {
            onToggle(path);
        }
    };

    const handleRowClick = () => {
        if (hasChildren) {
            onToggle(path);
        }
    };

    const handleCategoryNameClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onCategoryClick(node.category);
    };

    const categoryName = getCorrectTranslation(locale, node.category, 'name');

    return (
        <>
            <div
                className={`border-border-default hover:bg-background-surface-hover flex h-12 cursor-pointer items-center ${shouldShowBorder ? 'border-b' : ''}`}
                onClick={handleRowClick}>
                <div
                    className='flex w-full items-center gap-4 px-4'
                    style={{ paddingLeft: `${16 + indentLevel}px` }}>
                    <div
                        className='flex size-5 shrink-0 items-center justify-center'
                        onClick={handleToggle}>
                        {hasChildren ? (
                            isExpanded ? (
                                <LuChevronDown
                                    className='text-foreground-default'
                                    size={16}
                                />
                            ) : (
                                <LuChevronRight
                                    className='text-foreground-default'
                                    size={16}
                                />
                            )
                        ) : (
                            <div className='w-4' />
                        )}
                    </div>
                    <div className='flex w-full items-center justify-between'>
                        <div
                            className='hover:text-brand-primary flex items-center gap-2 font-medium'
                            onClick={handleCategoryNameClick}>
                            <LuFolder />
                            <span>{categoryName}</span>
                        </div>
                        <div className='flex items-center gap-2 text-sm font-medium'>
                            {node.subcategoriesCount > 0 && (
                                <div className='bg-border-default inline-flex h-6 items-center justify-center rounded px-1 lowercase'>
                                    {node.subcategoriesCount}{' '}
                                    {node.subcategoriesCount === 1
                                        ? CommonMessage.OBJECTS.CATEGORY.SUB_CATEGORY
                                        : CommonMessage.OBJECTS.CATEGORY.SUB_CATEGORIES}
                                </div>
                            )}
                            {node.productsCount > 0 && (
                                <div className='bg-border-default inline-flex h-6 items-center justify-center rounded px-1 lowercase'>
                                    {node.productsCount}{' '}
                                    {node.productsCount === 1 ? CommonMessage.OBJECTS.PRODUCT.DEFAULT : CommonMessage.OBJECTS.PRODUCT.PLURAL}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {isExpanded && (
                <>
                    {node.subcategories.map((subcategoryNode, subIndex) => {
                        const isLastSubcategory = subIndex === node.subcategories.length - 1 && node.products.length === 0;
                        const isLastItem = isLast && isLastSubcategory;
                        const subcategoryPath = [...path, subcategoryNode.category.id];
                        return (
                            <CategoryRow
                                key={subcategoryPath.join('/')}
                                node={subcategoryNode}
                                depth={depth + 1}
                                path={subcategoryPath}
                                expandedCategories={expandedCategories}
                                onToggle={onToggle}
                                onCategoryClick={onCategoryClick}
                                onProductClick={onProductClick}
                                locale={locale}
                                isLast={isLastItem}
                            />
                        );
                    })}

                    {node.products.map((product, productIndex) => {
                        const isLastProduct = productIndex === node.products.length - 1;
                        const isLastItem = isLast && isLastProduct;
                        return (
                            <ProductRow
                                key={product.id}
                                product={product}
                                depth={depth + 1}
                                onProductClick={onProductClick}
                                locale={locale}
                                isLast={isLastItem}
                            />
                        );
                    })}

                    {node.isLoading && (
                        <div
                            className={`border-border-default flex h-12 items-center px-4 ${isLast ? '' : 'border-b'}`}
                            style={{ paddingLeft: `${16 + indentLevel + 24}px` }}>
                            <div className='text-foreground-muted'>{CommonMessage.FORMS.LOADING_PLACEHOLDER}...</div>
                        </div>
                    )}
                </>
            )}
        </>
    );
};
